// src/pages/Budget/BudgetForm.jsx
import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import { useBudgets } from "../../context/BudgetContext";
import { useProjects } from "../../context/ProjectContext";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../context/AuthContext";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import NavigationBlocker from "../../components/common/NavigationBlocker";

import { toInputDate } from "../../utils/dateUtils";

const BudgetForm = () => {
  const { addBudget, updateBudget, getBudgetById, budgets } = useBudgets();
  const { projects } = useProjects();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const loadedIdRef = React.useRef(null);

  const { checkAccess } = useAuth();

  const [isDirty, setIsDirty] = useState(false);

  // 🟢 THE FIX: Call the hook at the top level, before any early returns!
  useUnsavedChanges(isDirty);

  useEffect(() => {
    if (!checkAccess('financial.create')) {
        toast.error("Access Denied: You do not have permission to modify budgets.");
        navigate('/admin/BudgetPage');
    }
  }, [checkAccess, navigate]);

  const [formData, setFormData] = useState({
    projectId: "",
    clientName: "",
    laborCostEstimate: "",
    materialCostEstimate: "",
    equipmentCostEstimate: "",
    realCostEstimate: "",
    startDate: "",
    endDate: "",
    status: "Planned",
    notes: "",
  });

  // Load existing data if editing (safe against loops)
  useEffect(() => {
    if (isEditing && id) {
      if (loadedIdRef.current === String(id)) return;
      loadedIdRef.current = String(id);

      const loadBudget = async () => {
        let existing = null;
        if (getBudgetById) {
          existing = await getBudgetById(id);
        }
        if (!existing && budgets && budgets.length > 0) {
          existing = budgets.find((b) => String(b.id) === String(id));
        }
        if (existing) {
          setFormData({
            ...existing,
            projectId: existing.projectId || existing.project_id || "",
            clientName: existing.clientName || existing.client_name || "",
            laborCostEstimate: existing.laborCostEstimate || existing.labor_cost_estimate || "",
            materialCostEstimate: existing.materialCostEstimate || existing.material_cost_estimate || "",
            equipmentCostEstimate: existing.equipmentCostEstimate || existing.equipment_cost_estimate || "",
            realCostEstimate: existing.realCostEstimate || existing.real_cost_estimate || existing.operational_budget || "",
            status: existing.status || "Planned",
            notes: existing.notes || "",
            startDate: toInputDate(existing.startDate || existing.start_date),
            endDate: toInputDate(existing.endDate || existing.end_date),
          });
        }
      };
      loadBudget();
    }
  }, [id, isEditing, getBudgetById, budgets]);

  // Handle Project Selection (Auto-fill client)
  const handleProjectChange = (e) => {
      setIsDirty(true);
      const selectedPid = e.target.value;
      const proj = projects.find(p => String(p.id) === String(selectedPid));
      setFormData(prev => ({
          ...prev,
          projectId: selectedPid,
          clientName: proj ? (proj.clientName || proj.client_name || "") : ""
      }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setIsDirty(true);
    const numericFields = ["laborCostEstimate", "materialCostEstimate", "equipmentCostEstimate", "realCostEstimate"];
    
    if (numericFields.includes(name)) {
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.projectId) return toast.error("Please select a project!");

    const payload = {
        ...formData,
        laborCostEstimate: Number(formData.laborCostEstimate) || 0,
        materialCostEstimate: Number(formData.materialCostEstimate) || 0,
        equipmentCostEstimate: Number(formData.equipmentCostEstimate) || 0,
        realCostEstimate: Number(formData.realCostEstimate) || 0,
    };

    await addBudget(payload); 
    
    setIsDirty(false);
    toast.success(isEditing ? "Budget updated successfully!" : "Budget saved successfully!");
    setTimeout(() => navigate("/admin/BudgetPage"), 700);
  };

  // The early return that was causing the React Hook error
  if (!checkAccess('financial.create')) return null;

  return (
    <Layout>
      <div className="p-6 bg-white min-h-screen">
        <h1 className="text-base font-bold mb-6">{isEditing ? "Edit Budget Limits" : "Set New Budget"}</h1>
        <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">

          {/* Select Project */}
          <div>
            <label className="block text-sm font-medium mb-2">Project</label>
            <select 
                name="projectId" 
                value={formData.projectId} 
                onChange={handleProjectChange} 
                required 
                disabled={isEditing}
                className={`w-full border rounded-md px-3 py-2 ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            >
              <option value="">-- Select Project --</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Client Name (Read Only) */}
          <div>
            <label className="block text-sm font-medium mb-2">Client Name</label>
            <input 
                type="text" 
                name="clientName" 
                value={formData.clientName} 
                readOnly 
                className="w-full border rounded-md px-3 py-2 bg-gray-50 text-gray-500"
            />
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <h3 className="text-blue-800 font-semibold mb-3">Planned Expense Limits</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Labor */}
                <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Labor Budget</label>
                    <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                        <input type="text" name="laborCostEstimate" value={formData.laborCostEstimate} onChange={handleChange} className="w-full border rounded-md pl-7 pr-3 py-2"/>
                    </div>
                </div>
                {/* Material */}
                <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Material Budget</label>
                    <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                        <input type="text" name="materialCostEstimate" value={formData.materialCostEstimate} onChange={handleChange} className="w-full border rounded-md pl-7 pr-3 py-2"/>
                    </div>
                </div>
                {/* Equipment */}
                <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Equipment Budget</label>
                    <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                        <input type="text" name="equipmentCostEstimate" value={formData.equipmentCostEstimate} onChange={handleChange} className="w-full border rounded-md pl-7 pr-3 py-2"/>
                    </div>
                </div>
                {/* Operational/Other */}
                <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Operational/Other</label>
                    <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                        <input type="text" name="realCostEstimate" value={formData.realCostEstimate} onChange={handleChange} className="w-full border rounded-md pl-7 pr-3 py-2"/>
                    </div>
                </div>
              </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Start Date</label>
              <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} className="w-full border rounded-md px-3 py-2"/>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">End Date</label>
              <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} className="w-full border rounded-md px-3 py-2"/>
            </div>
          </div>

          {/* Status & Notes */}
          <div className="grid grid-cols-1 gap-4">
            <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <select name="status" value={formData.status} onChange={handleChange} className="w-full border rounded-md px-3 py-2">
                <option>Planned</option>
                <option>Ongoing</option>
                <option>Completed</option>
                <option>Delayed</option>
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium mb-2">Notes</label>
                <textarea name="notes" rows="3" value={formData.notes} onChange={handleChange} className="w-full border rounded-md px-3 py-2"/>
            </div>
          </div>

          <div className="pt-4 flex gap-3">
            <button type="button" onClick={() => navigate('/admin/BudgetPage')} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700">{isEditing ? "Update Budget" : "Save Budget"}</button>
          </div>

        </form>
      </div>

      <NavigationBlocker isDirty={isDirty} />
    </Layout>
  );
};

export default BudgetForm;